import { useCallback, useState } from "react";
import { Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { PausedBanner } from "./PausedBanner";
import { CommandPalette } from "@/components/domain/CommandPalette";
import { ShortcutHelp } from "@/components/domain/ShortcutHelp";
import { GeminiChatFAB } from "@/components/domain/GeminiChatFAB";
import { useShortcuts, matchKey, matchCtrlKey } from "@/hooks/useShortcuts";
import { useAppDispatch } from "@/store";
import { api } from "@/store/api";
import { useTheme } from "@/hooks/useTheme";
import { toast } from "sonner";

export function AppShell() {
  const [theme] = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dispatch = useAppDispatch();

  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const closeHelp = useCallback(() => setHelpOpen(false), []);

  const onRefresh = useCallback(() => {
    // Refetch every active query in the API cache.
    dispatch(api.util.invalidateTags([
      "Status", "Cycles", "Pending", "Trades", "Decisions",
      "Gatekeeper", "Tuning", "PortfolioPnL",
      "Settings",
    ]));
    toast.info("Refreshing all views");
  }, [dispatch]);

  useShortcuts([
    {
      match: matchCtrlKey("k"),
      handler: (e) => {
        e.preventDefault();
        setPaletteOpen(true);
      },
      description: "Open command palette",
    },
    {
      match: (e) => e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey,
      handler: (e) => {
        e.preventDefault();
        setHelpOpen(true);
      },
      description: "Shortcut help",
    },
    {
      match: matchKey("r"),
      handler: () => onRefresh(),
      description: "Refresh status",
    },
  ]);

  return (
    <div className="min-h-screen flex bg-bg text-fg">
      <Sidebar
        mobileOpen={mobileMenuOpen}
        onCloseMobile={() => setMobileMenuOpen(false)}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <Topbar onOpenMobileMenu={() => setMobileMenuOpen(true)} />
        <PausedBanner />
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-7xl p-4 md:p-6 space-y-4">
            <Outlet />
          </div>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={closePalette} />
      <ShortcutHelp open={helpOpen} onClose={closeHelp} />
      <GeminiChatFAB />
      <Toaster theme={theme === "light" ? "light" : "dark"} position="bottom-right" richColors />
    </div>
  );
}
